import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  SubNav,
  Loader,
  useAPI,
  Grid,
  Stat,
  Card,
  ProgressBar,
} from "components/lib";
import { useParams } from "react-router-dom";
import { scrubSurveyImages } from "components/survey/formTheme";
import { gcpDownloadUrl } from "components/survey/gcpDownloadUrl";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/defaultV2.css";
import { applyStackgoTheme } from "components/survey/stackgo-theme";
import JSZip from "jszip";
import { buildVerificationNavBarItems } from "./nav-bar-items";
import { ScoringSummary, ScoreOverrideControl } from "./scoringSummary";
const Axios = require("axios");

StylesManager.applyTheme("defaultV2");

// Cap concurrent attachment downloads so a response with many files doesn't
// flood the browser with requests (and blow up memory) all at once.
const DOWNLOAD_CONCURRENCY = 4;

export function AdminResponseView(props) {
  const params = useParams();
  const response = useAPI(`/api/response/${params.id}`);
  const verification = useAPI(`/api/verification/${params.verification_id}`);
  const tranche2Permission = useAPI("/api/integration/tranche2/settings/can-complete");
  const canCompleteRiskAssessment = tranche2Permission?.data?.can_complete === true;
  const [navBarItems, setNavBarItems] = useState([]);
  const [formData, setFormData] = useState([]);
  const [createdAt, setCreatedAt] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [responseData, setResponseData] = useState({});
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [scoringSummary, setScoringSummary] = useState(null);
  // Attachment-download progress (drives the inline progress cue below).
  const [downloadTotal, setDownloadTotal] = useState(0);
  const [downloadDone, setDownloadDone] = useState(0);

  // Kept fresh every render so the memoized SurveyJS model's navigation-item
  // actions (Save as PDF / Download Files) always read the latest files/response
  // instead of the values captured when the model was first built.
  const latest = useRef({ files, response, responseData });
  latest.current = { files, response, responseData };

  // Guards the attachment-download run so it fires once per loaded response
  // (not again when the separate form-definition fetch settles).
  const hydratedForRef = useRef(null);

  // The component instance is reused across /response/:id navigations (no remount),
  // so reset per-response state and show the loader when the id changes — otherwise
  // a prior response's answers, _poa card, attachments, or progress can bleed into
  // the next one (e.g. navigating to a response whose data is empty).
  useEffect(() => {
    setLoading(true);
    setResponseData({});
    setFiles([]);
    setDownloadTotal(0);
    setDownloadDone(0);
  }, [params.id]);

  useEffect(() => {
    if (!response.loading && response.data) {
      setScoringSummary(response.data?.[0]?.scoring || null);
    }
  }, [response]);

  useEffect(() => {
    if (!verification.loading && verification.data) {
      setNavBarItems(
        buildVerificationNavBarItems(
          verification.data.verification[0],
          { id: params.verification_id },
          { canCompleteRiskAssessment }
        )
      );
    }
  }, [verification, canCompleteRiskAssessment, params.verification_id]);

  useEffect(() => {
    const getFormData = async () => {
      const formResponse = await Axios.get(
        "/api/form/" + response?.data?.[0]?.form_id
      );
      return formResponse;
    };

    if (!response.loading && response.data) {
      getFormData().then((formResponse) => {
        setFormData(formResponse?.data?.data?.[0]);
      });
    }
  }, [response]);

  useEffect(() => {
    if (response.data?.[0]?.created_at) {
      const date = new Date(response.data?.[0]?.created_at);
      setCreatedAt(date.toLocaleDateString());
    }

    if (response.data?.[0]?.updated_at) {
      const date = new Date(response.data?.[0]?.updated_at);
      setUpdatedAt(date.toLocaleDateString());
    }
  }, [response]);

  useEffect(() => {
    // Walk response data and hydrate any GCP-referenced files (including those
    // nested inside paneldynamic panels). Downloads are capped and committed in
    // chunks so we never queue hundreds of requests at once, and the component
    // re-renders a bounded number of times rather than once per file.
    if (response.loading || !response.data) return undefined;
    // Only hydrate once per distinct loaded response (useAPI returns a stable
    // object once settled), so the download run isn't repeated.
    if (hydratedForRef.current === response) return undefined;
    hydratedForRef.current = response;

    let cancelled = false;

    const isDataUrl = (s) => typeof s === "string" && s.startsWith("data:");

    const looksLikeGcpFile = (n) =>
      n &&
      typeof n === "object" &&
      typeof n.key === "string" &&
      typeof n.name === "string";

    const collectFiles = (node, acc) => {
      if (!node) return;
      if (Array.isArray(node)) {
        if (node.length && node.every(looksLikeGcpFile)) {
          node.forEach((f) => acc.push(f));
          return;
        }
        node.forEach((child) => collectFiles(child, acc));
        return;
      }
      if (typeof node === "object") {
        Object.values(node).forEach((child) => collectFiles(child, acc));
      }
    };

    const hydrate = async () => {
      const original = response.data?.[0]?.data;
      // The server needs the response that lists a key whose prefix the
      // client's browser made (KTD9).
      const responseId = response.data?.[0]?.id;

      if (!original || Object.keys(original).length === 0) {
        setResponseData({});
        setLoading(false);
        return;
      }

      // Work on a clone so we never mutate the useAPI-owned response object.
      const source = JSON.parse(JSON.stringify(original));

      // Show the form (text/number fields) immediately; previews fill in as
      // attachments download in the background.
      setResponseData(source);
      setLoading(false);

      const allFiles = [];
      collectFiles(source, allFiles);
      const toFetch = allFiles.filter((f) => !isDataUrl(f.content));

      setDownloadTotal(toFetch.length);
      setDownloadDone(0);
      if (toFetch.length === 0) return;

      for (let i = 0; i < toFetch.length && !cancelled; i += DOWNLOAD_CONCURRENCY) {
        const chunk = toFetch.slice(i, i + DOWNLOAD_CONCURRENCY);

        await Promise.all(
          chunk.map(async (file) => {
            try {
              const fileResponse = await Axios.get(
                gcpDownloadUrl({ key: file.key, name: file.name, responseId }),
                // Bound each download so one hung/slow GCS read can't stall the
                // sequential chunk loop and freeze progress for every later file.
                { timeout: 60000 }
              );
              file.content = fileResponse.data.blob;
            } catch (err) {
              console.error("Failed to download file", file.key, err);
            }
          })
        );

        if (cancelled) return;

        // Commit once per chunk (not per file) to bound re-renders.
        setFiles((prev) => {
          const next = [...prev];
          chunk.forEach((file) => {
            if (!next.find((f) => f.key === file.key && f.name === file.name)) {
              next.push(file);
            }
          });
          return next;
        });
        setResponseData({ ...source });
        setDownloadDone(Math.min(i + DOWNLOAD_CONCURRENCY, toFetch.length));
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [response]);

  // Download the PDF from the server so it matches the emailed attachment exactly
  // (shared generator in helper/buildResponsePdf.js — single source of truth).
  const savePdf = useCallback(async () => {
    try {
      const res = await Axios.get(`/api/response/${params.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${params.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download PDF", err);
      alert("Failed to download PDF. See console for details.");
    }
  }, [params.id]);

  // Build the SurveyJS model ONCE per form definition. Rebuilding it every render
  // (the previous behaviour) allocated a heavyweight model per render and made the
  // progress-driven re-renders below prohibitively expensive.
  const survey = useMemo(() => {
    const formSchema = response.data?.[0]?.form_data;
    if (!formSchema) return null;

    const model = scrubSurveyImages(new Model(formSchema));
    applyStackgoTheme(model);
    model.mode = "display";

    model.onDownloadFile.add((sender, options) => {
      // Hand back the inlined data URL set by the hydration step; if not yet
      // hydrated, resolve empty so the survey moves on (the preview fills in once
      // survey.data updates with the downloaded content).
      if (typeof options.content === "string" && options.content.startsWith("data:")) {
        options.callback("success", options.content);
        return;
      }
      options.callback("success", "");
    });

    model.addNavigationItem({
      id: "pdf-export",
      title: "Save as PDF",
      action: () => savePdf(),
    });

    model.addNavigationItem({
      id: "download-files",
      title: "Download Files",
      action: () => {
        const {
          files: latestFiles,
          response: latestResponse,
          responseData: latestResponseData,
        } = latest.current;

        // Build a zip containing all attached form file questions (original filenames preserved)
        const zip = new JSZip();

        const usedNames = new Map();

        const ensureUniqueName = (name) => {
          if (!usedNames.has(name)) {
            usedNames.set(name, 1);
            return name;
          }
          const count = usedNames.get(name);
          const parts = name.split(".");
          const ext = parts.length > 1 ? parts.pop() : "";
          const base = parts.join(".");
          let candidate;
          let i = count;
          do {
            candidate = ext ? `${base}(${i}).${ext}` : `${base}(${i})`;
            i++;
          } while (usedNames.has(candidate));
          usedNames.set(name, i);
          usedNames.set(candidate, 1);
          return candidate;
        };

        const dataUrlToBase64 = (dataUrl) => {
          if (!dataUrl) return null;
          // data URL pattern: data:<mime>;base64,<payload>
          if (dataUrl.startsWith("data:")) {
            const commaIndex = dataUrl.indexOf(",");
              if (commaIndex === -1) return null;
              return dataUrl.substring(commaIndex + 1); // raw base64
          }
          // already raw base64? Heuristic: only base64 charset and length multiple of 4
          if (/^[A-Za-z0-9+/=\n\r]+$/.test(dataUrl)) return dataUrl.replace(/\s/g, "");
          return null;
        };

        // Heuristic to decide if an object represents a single file
        const isFileLikeObject = (obj) => {
          if (!obj || typeof obj !== "object") return false;
          const hasName = "name" in obj || "filename" in obj;
          const hasContent = "content" in obj || "data" in obj;
          return hasName && hasContent;
        };

        const collected = [];

        const traverse = (node) => {
          if (!node) return;
          if (Array.isArray(node)) {
            // SurveyJS file question stores an array of file objects
              if (node.length && node.every((n) => isFileLikeObject(n))) {
                node.forEach((f) => collected.push(f));
                return; // don't recurse inside file objects
              }
              node.forEach(traverse);
              return;
          }
          if (isFileLikeObject(node)) {
            collected.push(node);
            return;
          }
          if (typeof node === "object") {
            Object.values(node).forEach(traverse);
          }
        };

        // Traverse the HYDRATED response data (the clone whose file objects receive
        // their downloaded `content`). Deferred-upload flows (e.g. McKinley) store
        // cloud files as { name, type, url, key } with no `content` key, so walking
        // the pristine response object would collect nothing — walk the hydrated copy
        // so both cloud and inline attachments are captured with content.
        traverse(latestResponseData);

        // Merge with already downloaded 'files' state (which should contain fetched content for cloud stored ones)
        const byKey = new Map();
        latestFiles.forEach((f) => {
          if (f.key) byKey.set(f.key, f);
        });

        const finalFileEntries = [];
        collected.forEach((fileObj) => {
          // prefer enriched file from state if matching key
          let enriched = fileObj;
          if (fileObj.key && byKey.has(fileObj.key)) {
            enriched = { ...fileObj, ...byKey.get(fileObj.key) };
          }
          finalFileEntries.push(enriched);
        });

        if (finalFileEntries.length === 0) {
          alert("No file attachments detected in this response.");
          return;
        }

        finalFileEntries.forEach((file) => {
          // Accept priority: content -> data
          const dataField = file.content || file.data;
          const base64 = dataUrlToBase64(dataField);
          if (!base64) return; // skip if content not available yet (e.g., still needs download)

          // Determine filename
          const rawName = file.name || file.filename || file.key || "file";
          const uniqueName = ensureUniqueName(rawName);
          zip.file(uniqueName, base64, { base64: true });
        });

        // Manifest
        const manifest = finalFileEntries.map((f) => ({
          name: f.name || f.filename,
          key: f.key,
          type: f.type,
          hasContent: !!(f.content || f.data),
        }));
        zip.file("manifest.json", JSON.stringify(manifest, null, 2));

        zip
          .generateAsync({ type: "blob" })
          .then((content) => {
            const url = URL.createObjectURL(content);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${latestResponse?.data?.[0]?.id || "response"}-files.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          })
          .catch((err) => {
            console.error("Failed to create zip", err);
            alert("Failed to generate ZIP file. See console for details.");
          });
      },
    });

    return model;
    // savePdf is stable (useCallback); latest is a ref. Rebuild only when the
    // form definition changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response.data?.[0]?.form_data, savePdf]);

  // Push the latest response data into the (stable) model as previews hydrate.
  useEffect(() => {
    if (survey) survey.data = responseData;
  }, [survey, responseData]);

  if (loading) {
    return <Loader />;
  }

  const downloading = downloadTotal > 0 && downloadDone < downloadTotal;

  return (
    <Fragment>
      <SubNav items={navBarItems} />

      <Grid cols="4">
        <Stat
          loading={response.loading}
          value={response.data?.[0]?.active === true ? "Yes" : "No"}
          label="active"
          icon="activity"
        />
        <Stat
          // loading={formData?.name}
          value={formData?.name}
          label="form name"
          icon="tag"
        />
        <Stat
          loading={response.loading}
          value={createdAt}
          label="created at"
          icon="calendar"
        />
        <Stat
          loading={response.loading}
          value={updatedAt}
          label="updated at"
          icon="calendar"
        />
      </Grid>

      {downloading && (
        <Card>
          <ProgressBar
            label={`Downloading attachments… ${downloadDone} of ${downloadTotal}`}
            progress={`${Math.round((downloadDone / downloadTotal) * 100)}%`}
          />
        </Card>
      )}

      {responseData?._poa && (
        <Card loading={response?.loading} title="Proof of Address Extraction">
          <div>
            <p>
              <strong>Name:</strong> {responseData._poa.name || "N/A"}
            </p>
            <p>
              <strong>Street:</strong> {responseData._poa.street || "N/A"}
            </p>
            <p>
              <strong>City:</strong> {responseData._poa.city || "N/A"}
            </p>
            <p>
              <strong>State:</strong> {responseData._poa.state || "N/A"}
            </p>
            <p>
              <strong>Country:</strong> {responseData._poa.country || "N/A"}
            </p>
            <p>
              <strong>Zip:</strong> {responseData._poa.zip || "N/A"}
            </p>
            <p>
              <strong>Issue Date:</strong>{" "}
              {responseData._poa.issueDate || "N/A"}
            </p>
            <p>
              <strong>Full Address:</strong>{" "}
              {responseData._poa.fullAddress || "N/A"}
            </p>
          </div>
        </Card>
      )}

      {scoringSummary && (
        <Card loading={response?.loading}>
          <div className="space-y-3">
            <ScoringSummary scoring={scoringSummary} />
            <ScoreOverrideControl
              responseId={params.id}
              scoring={scoringSummary}
              onUpdated={(data) => {
                if (data?.scoring) setScoringSummary(data.scoring);
              }}
            />
          </div>
        </Card>
      )}

      <Card loading={response?.loading}>
        {!response?.loading && survey && <Survey model={survey} />}
      </Card>
    </Fragment>
  );
}
